import { useContext } from 'react';
import { Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { Button, Loader } from '@aws-amplify/ui-react';
import {
  TbArrowBackUp,
  TbArrowForwardUp,
  TbBrush,
  TbCircle,
  TbPointer,
  TbRectangle,
} from 'react-icons/tb';
import { SegmentField } from 'src/custom-components/SegmentField';
import { NavigatorBeaconsPanel } from '../components/navigator/NavigatorBeaconsPanel';
import { NavigatorMap } from '../components/navigator/NavigatorMap';
import { NavigatorPanel } from '../components/navigator/NavigatorPanel';
import { NavigatorInstitutions } from '../components/navigator/NavigatorInstitutions';
import { NavigatorPeople } from '../components/navigator/NavigatorPeople';
import { NavigatorTerritoriesPanel } from '../components/navigator/NavigatorTerritoriesPanel';
import { TerritoryMap } from '../components/navigator/TerritoryMap';
import {
  DEFAULT_NAVIGATOR_QUERY,
  hasNavigatorQuery,
  serializeNavigatorQuery,
} from '../components/navigator/navigator-query';
import { GlobalContext } from '../global-context';
import General from '../utils/GeneralUtils';
import { useApiActivityStore } from '../zustand/useApiActivityStore';
import {
  BeaconTool,
  hasBeaconEdits,
  hasTerritoryEdits,
  useNavigatorStore,
} from '../zustand/useNavigatorStore';
import './Navigator.scss';

/** What the Navigator is for: searching, managing beacons, or managing territories. */
export type NavigatorMode = 'search' | 'beacons' | 'territories';

/** What the main area shows: the map, or a list of the people or institutions the query yields. */
export type NavigatorView = 'map' | 'people' | 'institutions';

const VIEWS: { id: NavigatorView; label: string }[] = [
  { id: 'map', label: 'Map' },
  { id: 'people', label: 'People' },
  { id: 'institutions', label: 'Institutions' },
];

/** Each mode and the views it has; the first is where it opens. */
const MODES: { id: NavigatorMode; label: string; views: NavigatorView[] }[] = [
  { id: 'search', label: 'Search', views: ['map', 'people', 'institutions'] },
  { id: 'beacons', label: 'Beacons', views: ['map'] },
  { id: 'territories', label: 'Territories', views: ['map'] },
];

/** How a click and a drag on the map pick districts while a beacon is edited. */
const TOOLS: { id: BeaconTool; label: JSX.Element; title: string }[] = [
  { id: 'pointer', label: <TbPointer />, title: 'Pointer' },
  { id: 'brush', label: <TbBrush />, title: 'Brush' },
  { id: 'rectangle', label: <TbRectangle />, title: 'Rectangle' },
  { id: 'oval', label: <TbCircle />, title: 'Oval' },
];

/** How the People view lists its people: one by one, or by the account they are at. */
const GROUPINGS = [
  { id: 'list', label: 'Flat list' },
  { id: 'account', label: 'Buying groups' },
];

/**
 * The Navigator at /navigator/<mode>/<view>: the map or a list, with the mode's panel on the
 * right. The mode and view are the path, so the query string stays the mode's own state (search:
 * the query, the lists' page and sort).
 */
export function Navigator() {
  const globalContext = useContext(GlobalContext);
  General.setTitle(document, 'Navigator', globalContext);

  const { mode: modeId, view } = useParams<{ mode: string; view: string }>();
  const location = useLocation();
  const navigate = useNavigate();
  const modeSearches = useNavigatorStore((state) => state.modeSearches);
  const setModeSearch = useNavigatorStore((state) => state.setModeSearch);
  const isWaiting = useApiActivityStore((state) => state.inFlight > 0);
  const tool = useNavigatorStore((state) => state.beaconTool);
  const setTool = useNavigatorStore((state) => state.setBeaconTool);
  // The toolbar edits what the map edits: in Beacons mode the beacon selected alone (for those
  // who may change it), in Territories mode the territory selected alone.
  const isTerritories = modeId === 'territories';
  const canUseTools = useNavigatorStore((state) =>
    isTerritories ? state.editingTerritoryId !== null : state.editingBeaconId !== null,
  );
  const isSaving = useNavigatorStore((state) =>
    isTerritories ? state.isSavingTerritory : state.isSavingBeacon,
  );
  const canUndo = useNavigatorStore((state) =>
    isTerritories ? state.territoryUndoStack.length > 0 : state.beaconUndoStack.length > 0,
  );
  const canRedo = useNavigatorStore((state) =>
    isTerritories ? state.territoryRedoStack.length > 0 : state.beaconRedoStack.length > 0,
  );
  const hasEdits = useNavigatorStore((state) =>
    isTerritories ? hasTerritoryEdits(state) : hasBeaconEdits(state),
  );
  const store = useNavigatorStore.getState;
  const undo = () => (isTerritories ? store().undoTerritoryEdit() : store().undoBeaconEdit());
  const redo = () => (isTerritories ? store().redoTerritoryEdit() : store().redoBeaconEdit());
  const saveEdits = () =>
    isTerritories ? store().saveTerritoryEdits() : store().saveBeaconEdits();

  const modes = MODES;
  const mode = modes.find((m) => m.id === modeId);

  // An unknown mode (a typo) opens the search; an unknown view the mode's first.
  if (!mode) return <Navigate to="/navigator/search/map" replace />;
  if (!mode.views.some((v) => v === view)) {
    return <Navigate to={`/navigator/${mode.id}/${mode.views[0]}${location.search}`} replace />;
  }

  // No query in the URL: the default one, so the search opens on a search.
  if (mode.id === 'search' && !hasNavigatorQuery(location.search)) {
    const params = new URLSearchParams(location.search);
    params.set('q', serializeNavigatorQuery(DEFAULT_NAVIGATOR_QUERY));
    return <Navigate to={`/navigator/search/${view}?${params.toString()}`} replace />;
  }

  // The mode's query string is left behind and the other's, as last left, comes back; the view
  // stays when the other mode has it.
  function onSwitchMode(selectedMode: string) {
    const next = modes.find((m) => m.id === selectedMode);
    if (!next || !mode) return;
    setModeSearch(mode.id, location.search);
    const nextView = next.views.find((v) => v === view) ?? next.views[0];
    navigate(`/navigator/${next.id}/${nextView}${modeSearches[next.id] ?? ''}`);
  }

  // The query string goes along: the query, the lists' page and sort.
  function onSwitchView(selectedView: string) {
    navigate(`/navigator/${mode?.id}/${selectedView}${location.search}`);
  }

  // The grouping is the URL's ?group=account (absent: the flat list); a change starts the list
  // again from its first page.
  const grouping =
    new URLSearchParams(location.search).get('group') === 'account' ? 'account' : 'list';
  function onSwitchGrouping(selected: string) {
    const params = new URLSearchParams(location.search);
    if (selected === 'account') params.set('group', 'account');
    else params.delete('group');
    params.delete('page');
    params.delete('pageToken');
    navigate(`${location.pathname}?${params.toString()}`);
  }

  return (
    <div className="navigator-container">
      <div className="navigator-header">
        {modes.length > 1 && (
          <SegmentField
            mode="single"
            size="large"
            options={modes}
            selectedId={mode.id}
            onChange={onSwitchMode}
          />
        )}
        {mode.views.length > 1 && (
          <SegmentField
            mode="single"
            size="large"
            options={VIEWS.filter((v) => mode.views.includes(v.id))}
            selectedId={view ?? ''}
            onChange={onSwitchView}
          />
        )}
        {view === 'people' && (
          <SegmentField
            mode="single"
            size="large"
            options={GROUPINGS}
            selectedId={grouping}
            onChange={onSwitchGrouping}
          />
        )}
        {/* Any API request in flight, after the switches */}
        <div
          className={`navigator-activity ${isWaiting ? 'navigator-activity--active' : ''}`}
          aria-hidden={!isWaiting}
        >
          <Loader size="large" filledColor="var(--navigator-activity-color)" />
        </div>
      </div>

      <div className="navigator-content">
        <div className="navigator-main">
          {view === 'map' && (mode.id === 'territories' ? <TerritoryMap /> : <NavigatorMap />)}
          {view === 'people' && <NavigatorPeople />}
          {view === 'institutions' && <NavigatorInstitutions />}
        </div>

        {mode.id === 'search' && <NavigatorPanel />}
        {mode.id === 'beacons' && <NavigatorBeaconsPanel />}
        {mode.id === 'territories' && <NavigatorTerritoriesPanel />}
      </div>

      {/* The map's edit toolbar, left of the right panel's top: for the one beacon or territory
          selected; territories take whole states, so no selection tools */}
      {(mode.id === 'beacons' || mode.id === 'territories') && view === 'map' && (
        <div className="navigator-toolbar">
          <div className={`navigator-tools ${canUseTools ? '' : 'navigator-tools--disabled'}`}>
            {/* Undo and redo the edits on the map: actions, so no option is ever the selected one */}
            <SegmentField
              mode="single"
              size="large"
              options={[
                {
                  id: 'undo',
                  label: <TbArrowBackUp />,
                  title: 'Undo (⌘Z)',
                  disabled: !canUndo || isSaving,
                },
                {
                  id: 'redo',
                  label: <TbArrowForwardUp />,
                  title: 'Redo (⇧⌘Z)',
                  disabled: !canRedo || isSaving,
                },
              ]}
              selectedId=""
              onChange={(id) => (id === 'undo' ? undo() : redo())}
              disabled={!canUseTools}
            />
            {!isTerritories && <span className="navigator-tools__divider" />}
            {!isTerritories && (
              <SegmentField
                mode="single"
                size="large"
                options={TOOLS}
                selectedId={tool}
                onChange={(id) => setTool(id as BeaconTool)}
                disabled={!canUseTools}
              />
            )}
          </div>
          <div className="navigator-toolbar__save-box">
            {/* Save the map's edits, apart from the tools: enabled once there are some */}
            <Button
              size="small"
              variation="primary"
              className="navigator-toolbar__save"
              isDisabled={!hasEdits || isSaving}
              isLoading={isSaving}
              loadingText="Saving"
              onClick={saveEdits}
            >
              Save
            </Button>
          </div>
        </div>
      )}
    </div>
  );
}
